import React, { useEffect, useRef } from 'react';
import * as Matter from 'matter-js';

const PerfectTowerBackground = () => {
    const sceneRef = useRef(null);

    useEffect(() => {
        const { Engine, Render, Runner, Bodies, Composite, Body, Events } = Matter;

        const engine = Engine.create();
        const world = engine.world;

        // Dimensions réelles du conteneur (pas window.innerHeight qui dépasse le slide)
        const W = sceneRef.current.offsetWidth;
        const H = sceneRef.current.offsetHeight;

        const render = Render.create({
            element: sceneRef.current,
            engine,
            options: {
                width: W,
                height: H,
                wireframes: false,
                background: 'transparent',
            }
        });

        // Murs et sol (identiques à tower.jsx)
        const wallOpts = { isStatic: true, render: { fillStyle: 'rgba(90, 54, 248, 0.42)' } };
        const ground = Bodies.rectangle(W / 2, H, W, 60, wallOpts);
        const rightWall = Bodies.rectangle(W * 0.96, H / 2, 20, H, wallOpts);
        const leftWall = Bodies.rectangle(W * 0.50, H / 2, 20, H, wallOpts);
        Composite.add(world, [ground, rightWall, leftWall]);

        // Paramètres de la tour
        const size = 40;
        const centerX = (W * 0.55 + W * 0.85) / 2;

        // Palette des pays (même que tower.jsx)
        const colors = [
            "#ff6b6b", "#1e40af", "#833a01", "#2e3949ff",
            "#8b5cf6", "#f59e0b", "white", "#059669"
        ];

        // Calculer le nombre de pièces pour remplir la hauteur
        const groundTop = H - 30;
        const targetTop = 120;
        const piecesNeeded = Math.ceil((groundTop - targetTop) / size);

        // Séquence prédéfinie : uniquement des I horizontales (briques parfaites)
        const sequence = [];
        for (let i = 0; i < piecesNeeded; i++) {
            sequence.push({ color: colors[i % colors.length] });
        }

        let spawnIndex = 0;
        let currentBlock = null;
        let isRunning = true;
        let settleFrames = 0;

        const spawnNext = () => {
            if (!isRunning || spawnIndex >= sequence.length) return;

            const piece = sequence[spawnIndex];
            const body = Bodies.rectangle(centerX, -50, size, size * 4, {
                restitution: 0.01,
                friction: 0.99,
                frictionStatic: 2.0,
                density: 0.005,
                label: 'perfect-block',
                render: { fillStyle: piece.color }
            });

            // Toujours horizontal, pas de rotation aléatoire
            Body.setAngle(body, Math.PI / 2);
            // Légère impulsion vers le bas pour accélérer la chute
            Body.setVelocity(body, { x: 0, y: 5 });

            Composite.add(world, body);
            currentBlock = body;
            settleFrames = 0;
            spawnIndex++;
        };

        // Vérifier le repos de chaque pièce avant de lancer la suivante
        Events.on(engine, 'beforeUpdate', () => {
            if (!currentBlock || currentBlock.isStatic) return;

            const speed = currentBlock.speed;
            const angularSpeed = currentBlock.angularSpeed;

            // Attendre que le bloc soit visible et presque immobile
            if (currentBlock.position.y > 0 && speed < 0.3 && angularSpeed < 0.01) {
                settleFrames++;

                // Attendre quelques frames pour confirmer l'immobilité
                if (settleFrames > 8) {
                    // Figer la pièce en position exacte
                    Body.setStatic(currentBlock, true);
                    Body.setVelocity(currentBlock, { x: 0, y: 0 });
                    Body.setAngularVelocity(currentBlock, 0);
                    // Corriger l'angle pour qu'il soit parfaitement horizontal
                    Body.setAngle(currentBlock, Math.PI / 2);

                    currentBlock = null;
                    // Lancer la pièce suivante
                    setTimeout(spawnNext, 150);
                }
            } else {
                settleFrames = 0;
            }
        });

        // Démarrage après un court délai
        setTimeout(spawnNext, 600);

        Render.run(render);
        const runner = Runner.create();
        Runner.run(runner, engine);

        // Gestion du redimensionnement
        const handleResize = () => {
            render.canvas.width = sceneRef.current?.offsetWidth || W;
            render.canvas.height = sceneRef.current?.offsetHeight || H;
        };
        window.addEventListener('resize', handleResize);

        return () => {
            isRunning = false;
            window.removeEventListener('resize', handleResize);
            Render.stop(render);
            Runner.stop(runner);
            if (render.canvas) render.canvas.remove();
            Composite.clear(world);
            Engine.clear(engine);
        };
    }, []);

    return (
        <div
            ref={sceneRef}
            style={{
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: -1,
            }}
        />
    );
};

export default PerfectTowerBackground;
